import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {WebsocketService} from "../../services/websocket.service";
import {filter, take} from "rxjs/operators";
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-scenario-list',
  standalone: true,
  imports: [TranslatePipe, CommonModule, NgFor, NgIf, FormsModule],
  templateUrl: './scenario-list.component.html',
  styleUrls: ['./scenario-list.component.scss']
})
export class ScenarioListComponent implements OnInit {
  scenarios: any[] | null = [];
  inputs: any;
  outputs: any | null = [];
  mac: string | null = null;
    
  deletingCondition: { scenarioIndex: number, conditionIndex: number } | null = null;
  addingCondition: { scenarioIndex: number, conditionIndex: number } | null = null;

  deletingAction: { scenarioIndex: number, actionIndex: number } | null = null;
  addingAction: { scenarioIndex: number, actionIndex: number } | null = null;
  swappingActions: { scenarioIndex: number, from: number, to: number, direction: 'up' | 'down' } | null = null;
  
  expandedScenarios: Set<number> = new Set();
  expandedSteppers: Set<number> = new Set();
  editingCondition: { scenarioId: number, index: number } | null = null;
  
  constructor (private route: ActivatedRoute,
               private router: Router,
               private websocketService: WebsocketService) {

  }
  
  trackByScenario(index: number, item: any) {
    return item?.id ?? index;
  }

  ngOnInit(): void {
    console.log('init scenarios')
    this.mac = this.route.snapshot.paramMap.get('mac');
    if (this.mac) {
      this.websocketService.getUserDevices().pipe(filter((devices: any) => devices != null),
                                                         take(1)).subscribe((devices) => {
        let device = devices.find((dev: any) => dev.mac === this.mac);
        if (device) {
          this.scenarios = device.config.scenarios;
          this.inputs = device.config.inputs.filter((input: any) => input.id < 16);
          this.outputs = device.config.outputs;
        }
        //console.log('scenarios ', this.scenarios);
      });
    }
  }

  toggleExpand(scenario: any) {
    const id = scenario.id;
    if (this.expandedScenarios.has(id)) {
      this.expandedScenarios.delete(id);
    } else {
      this.expandedScenarios.add(id);
    }
  }

  isExpanded(scenario: any): boolean {
    return this.expandedScenarios.has(scenario.id);
  }

  toggleStepperExpand(scenario: any) {
    const id = scenario.id;
    if (this.expandedSteppers.has(id)) {
      this.expandedSteppers.delete(id);
    } else {
      this.expandedSteppers.add(id);
    }
  }

  isStepperExpanded(scenario: any): boolean {
    return this.expandedSteppers.has(scenario.id);
  }

  back() {
    setTimeout(() => {
      const fromTab = (window.history && (window.history.state as any)?.fromTab) ? (window.history.state as any).fromTab : undefined;
      if (fromTab) {
        this.router.navigate(['/controller', this.mac], { state: { activeTab: fromTab } });
      } else {
        this.router.navigate(['/controller', this.mac]);
      }
        //this.location.back();
    }, 150);
  }

  save() {
    console.log('Saving scenarios:', this.scenarios);

    // for all conditions in all scenarios if ioType = RCIOInput then no outputEid present, and if ioType = RCIOOutput then no inputEid present - we need to fix this before sending to backend
    this.scenarios?.forEach(scenario => {
      scenario.conditions.forEach((condition: any) => {
        if (condition.ioType === 'RCIOInput') {          
          delete condition.outputEid;
        } else if (condition.ioType === 'RCIOOutput') {          
          delete condition.inputEid;
        }
      });
      scenario.actions.forEach((action: any) => {
        if (action.type === 'output') {          
          delete action.wait;
        } else if (action.type === 'wait') {
          delete action.action;
          delete action.outputEid;          
        }
      });
    });
    
    this.websocketService.sendMessage({
        type: 'UPDATESCENARIOS',
        payload: { mac: this.mac, scenarios: this.scenarios },
      });
    this.back();
  }

  addScenario() {
    const newId = Math.max(0, ...(this.scenarios?.map(s => s.id) || [])) + 1;
    const newScenario = {
      id: newId,
      name: `Scenario ${newId}`,
      conditions: [],
      actions: []
    };
    this.scenarios = [...(this.scenarios || []), newScenario];
    this.expandedScenarios.add(newId);
  }

  deleteScenario(scenario: any) {
    this.scenarios = (this.scenarios || []).filter(s => s.id !== scenario.id);
    this.expandedScenarios.delete(scenario.id);
  }
  
  getSortedActions(scenario: any) {
    return (scenario.actions || []).sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
  }
  
  isDeletingCondition(scenarioIndex: number, conditionIndex: number): boolean {
    return this.deletingCondition?.scenarioIndex === scenarioIndex && 
           this.deletingCondition?.conditionIndex === conditionIndex;
  }

  isDeletingAction(scenarioIndex: number, actionIndex: number): boolean {
    return this.deletingAction?.scenarioIndex === scenarioIndex && 
           this.deletingAction?.actionIndex === actionIndex;
  }

  isAddingCondition(scenarioIndex: number, conditionIndex: number): boolean {
    return this.addingCondition?.scenarioIndex === scenarioIndex && 
           this.addingCondition?.conditionIndex === conditionIndex;
  }

  isAddingAction(scenarioIndex: number, actionIndex: number): boolean {
    return this.addingAction?.scenarioIndex === scenarioIndex && 
           this.addingAction?.actionIndex === actionIndex;
  }

  moveActionUp(scenarioIndex: number, actionIndex: number): void {
    if (!this.scenarios) return;    
    const actions = this.scenarios[scenarioIndex].actions;
    if (actionIndex > 0) {
      this.swappingActions = { scenarioIndex, from: actionIndex, to: actionIndex - 1, direction: 'up' };
      setTimeout(() => {
        // Swap orders
        const currentOrder = actions[actionIndex].order;
        actions[actionIndex].order = actions[actionIndex - 1].order;
        actions[actionIndex - 1].order = currentOrder;
        // Swap positions in array
        [actions[actionIndex], actions[actionIndex - 1]] = [actions[actionIndex - 1], actions[actionIndex]];
        this.swappingActions = null;
      }, 300); // 10x slower
    }
  }

  moveActionDown(scenarioIndex: number, actionIndex: number): void {
    if (!this.scenarios) return;    
    const actions = this.scenarios[scenarioIndex].actions;
    if (actionIndex < actions.length - 1) {
      this.swappingActions = { scenarioIndex, from: actionIndex, to: actionIndex + 1, direction: 'down' };
      setTimeout(() => {
        // Swap orders
        const currentOrder = actions[actionIndex].order;
        actions[actionIndex].order = actions[actionIndex + 1].order;
        actions[actionIndex + 1].order = currentOrder;
        // Swap positions in array
        [actions[actionIndex], actions[actionIndex + 1]] = [actions[actionIndex + 1], actions[actionIndex]];
        this.swappingActions = null;
      }, 300); // 10x slower
    }
  }

  getSwapDirection(scenarioIndex: number, actionIndex: number): string | null {
    if (!this.swappingActions || this.swappingActions.scenarioIndex !== scenarioIndex) return null;
    if (this.swappingActions.from === actionIndex) return this.swappingActions.direction;
    if (this.swappingActions.to === actionIndex) return this.swappingActions.direction === 'up' ? 'down' : 'up';
    return null;
  }

  isSwappingAction(scenarioIndex: number, actionIndex: number): boolean {
    return !!(this.swappingActions && this.swappingActions.scenarioIndex === scenarioIndex &&
      (this.swappingActions.from === actionIndex || this.swappingActions.to === actionIndex));
  }

  canMoveUp(scenarioIndex: number, actionIndex: number): boolean {
    return actionIndex > 0;
  }

  canMoveDown(scenarioIndex: number, actionIndex: number): boolean {
    if (!this.scenarios) return false;
    const scenario = this.scenarios[scenarioIndex];
    if (!scenario) return false;
    return actionIndex < scenario.actions.length - 1;
  }

  removeCondition(scenarioIndex: number, conditionIndex: number): void {    
    this.deletingCondition = { scenarioIndex, conditionIndex };
    setTimeout(() => {
      if (this.scenarios) {
        this.scenarios[scenarioIndex].conditions.splice(conditionIndex, 1);
        this.deletingCondition = null;
      }
    }, 300); // Match this with the CSS animation duration
  }

  removeAction(scenarioIndex: number, actionIndex: number): void {    
    this.deletingAction = { scenarioIndex, actionIndex };
    setTimeout(() => {
      if (this.scenarios) {
        this.scenarios[scenarioIndex].actions.splice(actionIndex, 1);
        this.deletingAction = null;
      }
    }, 300); // Match this with the CSS animation duration
  }

  addAction(scenarioIndex: number): void {
    if (!this.scenarios) return;
    const actions = this.scenarios[scenarioIndex].actions;    
    const maxOrder = actions.reduce((max: number, action: any) => {
      return action.order > max ? action.order : max;
    }, -1);

    actions.push({
      outputEid: null,
      action: 'on',            
      order: maxOrder + 1
    });

    this.addingAction = { scenarioIndex, actionIndex: actions.length - 1 };
    setTimeout(() => {
      this.addingAction = null;
    }, 300); // Match this with the CSS animation duration
  }

  addCondition(scenarioIndex: number) {
    if (!this.scenarios) return;
    const conditions = this.scenarios[scenarioIndex].conditions;    
    
    conditions.push({
      ioType: 'RCIOInput',
      state: 'on',  
      //inputEid: this.inputs?.[0]?.eid || ''
    });

    this.addingCondition = { scenarioIndex, conditionIndex: conditions.length - 1 };
    setTimeout(() => {
      this.addingCondition = null;
    }, 300); // Match this with the CSS animation duration
  }
}